/**
 * Amazon CloudWatch RUM (Real User Monitoring) bootstrap.
 *
 * Configuration is injected via Vite `define`: `rum*` keys per mode in vite.config.mjs.
 * Alarms (manual AWS console steps): see awsAlarmSetup.txt in this folder — documentation only, not loaded at runtime.
 */
import { AwsRum, TelemetryEnum } from 'aws-rum-web';

import { getRumPageViewPayload } from './rumPageId';

let awsRumClient = null;

function envBool(val, defaultValue) {
  if (val === undefined || val === '') return defaultValue;
  if (typeof val === 'boolean') return val;
  return String(val).toLowerCase() === 'true' || val === '1';
}

function parseSampleRate(raw) {
  if (raw === undefined || raw === '') return 1;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) && n >= 0 && n <= 1 ? n : 1;
}

function parsePositiveInt(raw, fallback) {
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
}

/**
 * Initializes the RUM client once. Safe to call multiple times; only first successful init sticks.
 */
export function initRum() {
  if (typeof window === 'undefined') return;

  if (!envBool(process.env.rumEnabled, false)) {
    return;
  }

  const applicationId = process.env.rumApplicationId || '';
  const identityPoolId = process.env.rumIdentityPoolId || '';
  const region = process.env.rumRegion || '';

  if (!applicationId || !identityPoolId || !region) {
    console.warn(
      '[RUM] rumEnabled is true but rumApplicationId, rumIdentityPoolId, or rumRegion is missing (envMappings in vite.config.mjs).'
    );
    return;
  }

  if (awsRumClient) {
    return;
  }

  const applicationVersion = process.env.rumAppVersion || '1.0.0';
  const endpoint = (process.env.rumEndpoint && String(process.env.rumEndpoint).trim()) || `https://dataplane.rum.${region}.amazonaws.com`;

  try {
    // Secure cookies fail on http:// — RUM session cookies won't stick; dataplane still works once creds load.
    const cookieAttributes = {
      secure: typeof window !== 'undefined' && window.location?.protocol === 'https:'
    };

    // Aligns with AWS console snippet + SPA extras (disableAutoPageView, recordPageView in RumNavigationSync).
    awsRumClient = new AwsRum(applicationId, applicationVersion, region, {
      cookieAttributes,
      sessionSampleRate: parseSampleRate(process.env.rumSessionSampleRate),
      // Default SDK limit is low when many event types are enabled; keep headroom for perf + custom API error events.
      sessionEventLimit: parsePositiveInt(process.env.rumSessionEventLimit, 10000),
      eventCacheSize: parsePositiveInt(process.env.rumEventCacheSize, 2000),
      identityPoolId,
      endpoint,
      // Performance: navigation, web vitals, SPA timings; Resource plugin ignores fetch/XHR by default.
      // Errors: uncaught JS + recordError (not HTTP status — see InsightApiHttpError from Axios).
      // Http: failed fetch/XHR only (recordAllRequests: false — no successful-request latency stream).
      telemetries: [TelemetryEnum.Performance, TelemetryEnum.Errors, [TelemetryEnum.Http, { recordAllRequests: false }]],
      allowCookies: true,
      signing: envBool(process.env.rumSigning, true),
      guestRoleArn: process.env.rumGuestRoleArn || undefined,
      // We record route changes manually so SPA navigations map to distinct page IDs
      disableAutoPageView: true,
      // Emits synthetic navigation timing on SPA route changes so Performance > page load tables populate.
      // See https://github.com/aws-observability/aws-rum-web/issues/723 — timing is approximate.
      legacySPASupport: envBool(process.env.rumLegacySPASupport, true),
      enableXRay: envBool(process.env.rumEnableXRay, false),
      debug: envBool(process.env.rumDebug, false)
    });
    // Register landing page before window "load" navigation timing so metadata includes the stable route template.
    try {
      awsRumClient.recordPageView(getRumPageViewPayload(window.location.pathname || '/'));
    } catch (_) {
      /* ignore */
    }
    // eslint-disable-next-line no-console
    console.info('[RUM] Client initialized', { region, applicationIdPrefix: `${applicationId.slice(0, 8)}…` });
  } catch (e) {
    console.error('[RUM] Failed to initialize CloudWatch RUM client:', e);
    awsRumClient = null;
  }

  if (awsRumClient) {
    window.addEventListener(
      'unhandledrejection',
      (event) => {
        try {
          awsRumClient.recordError(event.reason);
        } catch (_) {
          /* ignore */
        }
      },
      { passive: true }
    );
  }
}

export function getAwsRum() {
  return awsRumClient;
}

/**
 * Records Insight API failures (HTTP 4xx/5xx only) — pathname only, no query string.
 * Network errors without a response are not emitted (team scope: 4xx/5xx only).
 */
export function recordInsightApiHttpError({ durationMs, method, httpStatus, path }) {
  const client = getAwsRum();
  if (!client || typeof durationMs !== 'number') return;
  const status = httpStatus ?? 0;
  if (status < 400 || status >= 600) return;
  try {
    client.recordEvent('InsightApiHttpError', {
      durationMs: Math.round(durationMs),
      method: (method || 'GET').toUpperCase(),
      httpStatus: status,
      path: path || 'unknown'
    });
  } catch (_) {
    /* ignore */
  }
}

/** URL pathname only for Axios config — avoids logging query strings with tokens or ids. */
export function getSafeApiPathFromAxiosConfig(config) {
  if (!config) return 'unknown';
  const url = config.url || '';
  const base = config.baseURL || '';
  try {
    const resolved = url.startsWith('http')
      ? url
      : new URL(url, base || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost')).href;
    return new URL(resolved).pathname || 'unknown';
  } catch {
    return 'unknown';
  }
}
